import { NgFor, NgIf } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AssetColorLabel,
  AssetDto,
  AssetStatus,
  GalleryAccessLevel,
  GalleryColorLabelDto,
  GalleryDto,
  GalleryFeedbackOption,
} from '@picflow/types';
import { Subscription } from 'rxjs';
import { ScreenSizes } from 'src/app/classes/screen-sizes.class';
import { GallerySettingsDialogComponent } from 'src/app/gallery/dialogs/gallery-settings-dialog/gallery-settings-dialog.component';
import { getAllowedAndEnabledFeedbackOptions } from 'src/app/lib/get-allowed-feedback-options';
import { sleep } from 'src/app/lib/sleep';
import { AssetService } from 'src/app/services/asset.service';
import { DialogService } from 'src/app/services/dialog.service';
import { GalleryService } from 'src/app/services/gallery.service';
import { StateService } from 'src/app/services/state.service';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { TranslateLabelPipe } from '../../../shared/pipes/translate-label.pipe';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { ApprovalLimitReachedDialogComponent } from '../../dialogs/approval-limit-reached-dialog/approval-limit-reached-dialog.component';
import { feedbackButtonSizes, fits } from './fits';

@Component({
  selector: 'app-asset-page-footer',
  templateUrl: './asset-page-footer.component.html',
  styleUrls: ['./asset-page-footer.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    ButtonDirective,
    TooltipDirective,
    SvgComponent,
    ContextMenuComponent,
    NgFor,
    ButtonMenuItemComponent,
    TranslatePipe,
    TranslateLabelPipe,
  ],
})
export class AssetPageFooterComponent implements OnChanges, OnDestroy, OnInit {
  @Input() asset: AssetDto;
  @Input() commentCount: number;
  @Input() gallery: GalleryDto;
  @Input() sideMenuOpen: boolean;
  @Input() currentLabel: GalleryColorLabelDto;
  @Input() colorLabels: GalleryColorLabelDto[] = [];
  @Input() accessLevel: GalleryAccessLevel;
  totalApproved: number = 0;
  approvalLimit: number = 0;
  disabledFeedbackItems: { icon: string; option: string; name: string }[] = [];
  gallerySub: Subscription;

  @Output() onToggleLike = new EventEmitter();
  @Output() onUpdateAssetStatus = new EventEmitter();
  @Output() onUpdateColorLabel = new EventEmitter();
  @Output() onToggleSideMenu = new EventEmitter();
  @Output() onStartAnnotation = new EventEmitter();

  @HostListener('window:resize', ['$event']) winResize() {
    if (!this.asset) return;
    this.calc();
  }

  display: { [propName: string]: 'full' | 'small' };

  constructor(
    private assetService: AssetService,
    private dialog: DialogService,
    private state: StateService,
    private galleryService: GalleryService
  ) {}

  ngOnInit(): void {
    this.gallerySub = this.galleryService.onUpdates.subscribe(async update => {
      if (!update) return;
      if (update[0] !== 'updated') return;
      if (update[1] !== this.gallery.id) return;
      await sleep(0);
      this.calc();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.calc();
    if (changes.asset) {
      this.calcApprovedTotal();
    }
  }

  private getFeedbackIcon = (option: GalleryFeedbackOption): string => {
    if (option === 'annotation') return 'annotation';
    if (option === 'comment') return 'comment';
    if (option === 'color_label') return 'tag';
    if (option === 'favourite') return 'star';
    if (option == 'flag') return 'flag';
  };

  private getFeedbackName = (option: GalleryFeedbackOption): string => {
    if (option === 'annotation') return 'Annotations';
    if (option === 'comment') return 'Comments';
    if (option === 'color_label') return 'Color Labels';
    if (option === 'favourite') return 'Favourites';
    if (option == 'flag') return 'Flags';
  };

  calc() {
    this.display = {};
    const enabledAndAllowedFeedbackOptions = getAllowedAndEnabledFeedbackOptions(
      this.gallery,
      this.state.plan,
      this.accessLevel
    );

    enabledAndAllowedFeedbackOptions.forEach(option => {
      if (!feedbackButtonSizes[option]) return;
      this.display[option] = 'small';
    });

    const feedbackOptionsThatCanShowInFooter: GalleryFeedbackOption[] = [
      'flag',
      'favourite',
      'color_label',
      'comment',
      'annotation',
    ];

    if (this.accessLevel === 'edit') {
      this.disabledFeedbackItems = feedbackOptionsThatCanShowInFooter
        .filter(option => {
          return !enabledAndAllowedFeedbackOptions.includes(option);
        })
        .map(option => {
          return { name: this.getFeedbackName(option), icon: this.getFeedbackIcon(option) + '-dotted', option };
        });
    }

    feedbackOptionsThatCanShowInFooter
      .filter(option => enabledAndAllowedFeedbackOptions.includes(option))
      .forEach(option => {
        if (fits({ ...this.display, [option]: 'full' }, this.disabledFeedbackItems, this.sideMenuOpen))
          this.display[option] = 'full';
      });

    if (!fits(this.display, this.disabledFeedbackItems, this.sideMenuOpen)) {
      if (ScreenSizes.screenSize() !== 'mobile' || !this.sideMenuOpen) {
        console.warn('too small');
      }
    }

    this.approvalLimit = Math.abs(this.gallery.approval_limits);
  }

  async calcApprovedTotal() {
    if (!this.gallery.approval_limits) return;
    this.totalApproved = await this.assetService.getApprovedAssetsCount(this.gallery.id);
  }

  toggleLike() {
    this.onToggleLike.emit();
  }

  async updateAssetStatus(status: AssetStatus) {
    if (status === 'approved') {
      if (this.approvalLimit && this.totalApproved >= this.approvalLimit) {
        const softLimit = this.gallery.approval_limits < 0;
        const resp = await this.dialog.open(ApprovalLimitReachedDialogComponent, {
          limit: this.approvalLimit,
          softLimit,
        });
        if (!resp) return;
      }
      this.totalApproved += 1;
    }
    if (status !== 'approved' && this.asset.status === 'approved') this.totalApproved -= 1;
    this.onUpdateAssetStatus.emit(status);
  }

  updateColorLabel(label: AssetColorLabel) {
    this.onUpdateColorLabel.emit(label);
  }

  toggleSideMenu() {
    this.onToggleSideMenu.emit('activity');
  }

  openReviewSettings() {
    this.dialog.open(GallerySettingsDialogComponent, { gallery: this.gallery, defaultMode: 'review' });
  }

  ngOnDestroy(): void {
    this.gallerySub.unsubscribe();
  }
}
